import _ from "lodash";
import mixpanel from "mixpanel-browser";
import {
  history,
  isMobile,
  isResponsiveWidth,
  setMixpanelProfileInfo,
  setCookie,
  getCookie,
  encryptId,
  setSentryProfile,
  getAppEnv,
  buildUrl,
  goToLoggedInUrl,
  logMixpanelEvent,
  createDomainLink,
  cmsIntentParams
} from "../../../helpers";
import { loginConstants } from "./ClinicLoginConstants";

import { postLogin, getClinicDoctors, createClinic } from "../../../services";

export const loginProcessing = isProcessing => ({
  type: loginConstants.LOGIN_PROCESSING,
  isProcessing
});
export const loginSuccess = user => ({
  type: loginConstants.LOGIN_SUCCESS,
  user
});
export const loginFailure = error => ({
  type: loginConstants.LOGIN_FAILURE,
  error
});
export const userLoggedIn = userSignedIn => ({
  type: loginConstants.USER_LOGGED_IN,
  userSignedIn
});

// export const showFreshChat = (clinicHasDoctors, clinic) => {
//   if (clinicHasDoctors) {
//     localStorage.setItem("open_chat_popup", true);
//   }
//   setFreshChatUser(clinic);
// };

export const setCookieRelatedData = responseObj => {
  const { user } = responseObj.data;
  const appEnv = getAppEnv();

  setCookie(`${appEnv}_authToken`, `Bearer ${responseObj.auth_token}`);
  setCookie(`${appEnv}_id_tkn`, `${responseObj.refresh_token}`);

  // check if current language is being selected by the user, hence store it in cookies
  const selectedLanguage = getCookie(`${appEnv}_current_language`)
    ? getCookie(`${appEnv}_current_language`)
    : "en";

  setCookie(`${appEnv}_current_language`, selectedLanguage);
  setCookie(`${appEnv}_user`, JSON.stringify({ id: user.id }));
  // setCookie(`${getAppEnv()}_is_provider`, true);
};

/**
 * @function loginAction
 * @param phoneNumber
 * @param password
 * @desc This logins the user.
 */
const countryCode = "91";
export const loginAction = (phoneNumber, password, utmParams) => {
  return dispatch => {
    // if the user has signed in from mobile, then prompt user to continue rest of the app through web
    dispatch(loginProcessing(true));
    postLogin(phoneNumber, password, utmParams).then(response => {
      // login successful
      if (response.data && response.data.data && response.data.data.user) {
        const responseObj = response.data;
        // setCookieRelatedData(responseObj);
        dispatch(loginProcessing(false));
        redirectToLoggedInSite(responseObj, null, null, "login");
      } else if (response.data && response.data.errors) {
        const { errors } = response.data;
        let error = errors;
        if (Array.isArray(errors)) {
          // eslint-disable-next-line prefer-destructuring
          error = errors[0];
        }

        switch (error.source) {
          case "account":
            if (error.title.toLowerCase() === "account not verified") {
              // Unverified user tries to sign in - Take user to the verification page
              history.push(
                `/verify-clinic?ph=${phoneNumber}&cc=${countryCode}`
              );
            }
            break;
          default:
            dispatch(loginProcessing(false));
            dispatch(loginFailure(error));
            dispatch(userLoggedIn(false));
        }
      }
    });
  };
};

export const isClinicAccountCreated = clinicAccountPresent => ({
  type: loginConstants.HAS_CLINIC_ACCOUNT,
  hasClinicAccount: clinicAccountPresent
});

export const magicLinkSignInAction = (
  responseObj,
  pageName,
  videoUrl = null,
  sendgridCategory = null,
  link_type,
  clinic_id = null
) => {
  return dispatch => {
    const appEnv = getAppEnv();
    if (link_type === "billing") {
      dispatch(showLoader(false));
      setCookie(`${appEnv}_link_type`, "billing");
    }

    if (pageName) {
      setCookie(`${appEnv}_page_name`, pageName);
    }
    if (videoUrl) {
      setCookie(`${appEnv}_video_url`, videoUrl);
    }
    if (sendgridCategory) {
      setCookie(`${appEnv}_sendgrid_category`, sendgridCategory);
    }
    // list all the clinic related to the user and set cookie related to "selectedClinic"
    const { user } = responseObj.data;
    // fetch clinic owner from "user" object

    const clinicOwnerAccounts = user.user_accounts.filter(ua => {
      return ua.account_type === "clinic" && ua.role === "owner";
    });

    if (clinicOwnerAccounts.length === 1) {
      const selectedClinicObj = {
        id: clinicOwnerAccounts[0].clinic_id,
        name: clinicOwnerAccounts[0].clinic_name,
        role: clinicOwnerAccounts[0].role,
        logo_url: clinicOwnerAccounts[0].logo_url,
        timezone: clinicOwnerAccounts[0].timezone,
        virtual_clinic: clinicOwnerAccounts[0].virtual_clinic
      };
      // set the clinic cookie only if clinic owner account === 1, this is will fetched as
      // "selectedClinic" in other kulcare projects ("cms", "growth")
      // if clinic owner account is more than 1 then we need to show the "select-clinic" screen
      // so will need not set the selected clinic in cookie
      setCookie(`${appEnv}_clinic`, JSON.stringify(selectedClinicObj));
    } else {
      setCookie(`${appEnv}_clinic`, null);
    }

    // setCookieRelatedData(responseObj);
    redirectToLoggedInSite(responseObj, link_type, clinic_id);
  };
};

/**
 * @function showLoader
 * @param showLoadingState
 * @desc This function handles showing and hide loader, on full page, when user comes first time in the project
 */
export const showLoader = showLoadingState => {
  return {
    type: loginConstants.SHOW_LOADER,
    showLoadingState
  };
};

/**
 * @function redirectDirectToApp
 * @param responseObj
 * @desc This function handles setting cookies, redirecting user to the app according to the domain link
 */
export const redirectDirectToApp = responseObj => {
  // set cookies for the user
  setCookieRelatedData(responseObj);
  // fetch user from responseObj
  const { user } = responseObj.data;
  // create the domain link, to where user has to be redirected
  const redirectUrl = createDomainLink(user);
  // handle the redirection of url according to created domain link
  handleRedirection(redirectUrl);
};

/**
 * @function handleRedirection
 * @param redirectUrl
 * @desc This function handles redirection of url according to created domain link
 */
export const handleRedirection = redirectUrl => {
  showLoader(false);
  const appEnv = getAppEnv();
  const returnUrl = getCookie(`${appEnv}_return_url`);
  if (returnUrl) window.open(returnUrl, "_self");
  else window.open(`${redirectUrl}`, "_self");
};

/**
 * @function redirectToPatientSite
 * @param "fromSignup", "responseObj"
 * @desc This function handles redirection of patient to logged in patient site
 */
export const redirectToPatientSite = async (fromSignup, responseObj) => {
  if (fromSignup) {
    // if the user is from signup page, then track the event and store onboard identifier in cookie
    mixpanel.track("Patient Onboarding Intiated");
    setCookie(`${getAppEnv()}_patient_onboard`, true);
  }
  setCookie(`${getAppEnv()}_is_patient`, true);
  // set all user related cookie data
  setCookieRelatedData(responseObj);
  // redirect user to patient site
  window.open(process.env.REACT_APP_PATIENT_SITE_BASE_URL, "_self");
};

/**
 * @function redirectToLoggedInSite
 * @param "responseObj", "link_type"
 * @desc This function handles redirecting user to respective page ("select-account/ CMS/ growth"),
 */
export const redirectToLoggedInSite = async (
  responseObj,
  link_type,
  clinic_id,
  fromAction
) => {
  // and then handle redirections
  let { user } = responseObj.data;
  const appEnv = getAppEnv(); // get current environment key, it will return string like "env", "stg", "prod"

  // This is important for showing the test mode functionality, shown in Manage practice 
  // set dummy clinic cookie if user has dummy clinic
  if (user?.dummy_clinic) {
    setCookie(
      `${appEnv}_dummy_clinic`,
      JSON.stringify({
        ...user.dummy_clinic,
        id: user.dummy_clinic.clinic_id,
        name: user.dummy_clinic.clinic_name
      })
    );
  }

  // list all the clinic related to the user and set cookie related to "selectedClinic"
  // fetch clinic owner from "user" object
  let clinicOwnerAccounts = [];
  let staffAccounts = [];
  let consultantAccounts = [];
  let hospitalAdminAccounts = [];
  if (user.user_accounts.length > 0) {
    // fetch clinic owner account
    clinicOwnerAccounts = user.user_accounts.filter(ua => {
      return (
        ua.account_type === "clinic" &&
        ua.role === "owner" &&
        !ua.virtual_clinic
      );
    });
    // fetch staff account
    staffAccounts = user.user_accounts.filter(ua => {
      return ua.account_type === "clinic" && ua.role === "staff";
    });
    // fetch hospital admin account
    hospitalAdminAccounts = user.user_accounts.filter(ua => {
      return ua.account_type === "clinic" && ua.role === "admin";
    });
    // fetch consoultant account
    consultantAccounts = user.user_accounts.filter(ua => {
      return ua.account_type === "clinic" && ua.role === "consultant";
    });
  }

  // handle clinic creation if no user account or no clinic owner account is present and signup intent is that related to CMS
  if (
    user.user_accounts.length === 0 ||
    (clinicOwnerAccounts.length === 0 &&
      staffAccounts.length === 0 &&
      hospitalAdminAccounts.length === 0 &&
      consultantAccounts.length === 0)
  ) {
    // create clinic first - this is to remove clinic creation step in CMS onboarding
    const finalObj = {
      clinic_details: {
        name: `Dr. ${user?.name}'s clinic`
      },
      onboarding: true,
      tkn: responseObj.auth_token
    };
    // onboarding related
    mixpanel.track("Onboarding Intiated");
    if (fromAction === "signup") {
      setCookie(`${appEnv}_user_onboarded`, `true`);
    }
    // hit the save profile api here, createClinic service allows creation of a clinic
    const response = await createClinic(finalObj);
    // fetch response
    if (response && response.data.success) {
      const clinicObj = response?.data?.data;
      user.user_accounts = clinicObj.user_accounts;
    }
  }

  // fetch clinic account
  const clinicAccounts = user.user_accounts.filter(ua => {
    return ua.account_type === "clinic";
  });
  // create user to initialize sentry
  const sentryUserObj = {
    id: user.id,
    name: clinicAccounts.length > 0 ? clinicAccounts[0].clinic_name : ""
  };
  // set sentry
  setSentryProfile(sentryUserObj);

  // If clinic owner account is present or user is signed up as "hospital_clinic", then set a clinic in cookie
  // to be shared in practices site
  if (
    clinicOwnerAccounts.length === 1 ||
    user.signed_up_as === "hospital_clinic"
  ) {
    // all these params should are important for correct functioning of practices site, 
    // as we keep a clinic selected in localstorage in Manage practice and growth
    // these keys in selected clinic should remain same in practices site project also
    // as it helps hide or show certain fields/ options in practices site
    const selectedClinicObj = {
      id: clinicOwnerAccounts[0].clinic_id,
      name: clinicOwnerAccounts[0].clinic_name,
      role: clinicOwnerAccounts[0].role,
      logo_url: clinicOwnerAccounts[0].logo_url,
      timezone: clinicOwnerAccounts[0].timezone,
      virtual_clinic: clinicOwnerAccounts[0].virtual_clinic,
      ipd_enabled: clinicOwnerAccounts[0].ipd_enabled
    };

    // set the clinic cookie only if clinic owner account === 1, this is will fetched as
    // "selectedClinic" in other kulcare projects ("cms" or "practices")
    // if clinic owner account is more than 1 then we need to show the "select-clinic" screen
    // so will need not set the selected clinic in cookie, if there is single clinic

    setCookie(`${appEnv}_clinic`, JSON.stringify(selectedClinicObj));
  }

  // if the user has signed up using a referrer
  const referrer = localStorage.getItem("referrer");
  let redirectUrl = "/";
  // in case user is not from mobile
  if (
    referrer !== "null" &&
    referrer !== "undefined" &&
    referrer !== null &&
    referrer !== undefined
  ) {
    // set the cookie related data first and then redirect to CMS site
    setCookieRelatedData(responseObj);
    // if redirect url is present then go to that requested url
    // build url
    redirectUrl = buildUrl(referrer);
    localStorage.removeItem("referrer");
    goToLoggedInUrl(redirectUrl);
  } else {
    if (link_type) {
      // set the cookie related data first and then redirect to "link_type" site
      setCookieRelatedData(responseObj);
      // if redirect url is present then go to that requested url
      // build url
      // Redirect to CMS directly for now for production
      redirectUrl = clinic_id
        ? buildUrl(link_type, clinic_id)
        : buildUrl(link_type);
      goToLoggedInUrl(redirectUrl);
    } else {
      // This is the simple scenerio when there is no referrer or link type present
      redirectDirectToApp(responseObj);
    }
  }
};

/**
 * @function logEvent
 * @param "responseObj", "fromAction", "appTypeParam"
 * @desc This function handles logging mixpanel event post login or signup
 */
export const logEvent = (
  responseObj,
  fromAction,
  appTypeParam,
  mixpanelFromParam = null
) => {
  // log mixpanel event
  if (fromAction == "login")
    logMixpanelEvent(
      "Logged In Successfully",
      null,
      null,
      null,
      responseObj.data.user.signed_up_as,
      responseObj.data.user.id
    );
  if (fromAction == "signup")
    logMixpanelEvent(
      "Signed Up Successfully",
      null,
      mixpanelFromParam,
      null,
      appTypeParam,
      responseObj.data.user.id
    );
};
